import { useState, useCallback, useEffect, useRef } from 'react';
import EmbedAPIClient from '@embedapi/core';
import StreamingJSONParser from '../utils/StreamingJSONParser';

// Polyfill AbortSignal.timeout if it doesn't exist
if (!AbortSignal.timeout) {
  AbortSignal.timeout = function timeout(ms) {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), ms);
    return controller.signal;
  };
}

const parser = new StreamingJSONParser();

const STORAGE_KEY = 'embedapi_chat_';
const DEFAULT_MESSAGE_LIMIT = 10;

export function useChat({
    agentId,
    service = 'openai',
    model = 'gpt-4o',
    initialMessages = [],
    onError,
    enableCache = true,
    messageLimit = DEFAULT_MESSAGE_LIMIT
}) {
    // Get cached messages for this agent
    const getCachedMessages = useCallback(() => {
        if (!enableCache) return initialMessages;
        try {
            const cached = sessionStorage.getItem(`${STORAGE_KEY}${agentId}`);
            const parsedMessages = cached ? JSON.parse(cached) : initialMessages;
            // Apply message limit to cached messages
            return parsedMessages.slice(-messageLimit);
        } catch (err) {
            console.warn('Failed to load cached messages:', err);
            return initialMessages;
        }
    }, [agentId, initialMessages, enableCache, messageLimit]);

    const [messages, setMessages] = useState(getCachedMessages);
    const currentMessage = useRef('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Cache messages with limit
    useEffect(() => {
        if (enableCache && agentId && messages.length > 0) {
            try {
                // Only cache up to messageLimit messages
                const messagesToCache = messages.slice(-messageLimit);
                sessionStorage.setItem(`${STORAGE_KEY}${agentId}`, JSON.stringify(messagesToCache));
            } catch (err) {
                console.warn('Failed to cache messages:', err);
            }
        }
    }, [messages, agentId, enableCache, messageLimit]);

    useEffect(() => {
        if (!agentId) {
            const errorMessage = { role: 'system', content: 'Error: agentId is required.' };
            setMessages(prev => [...prev, errorMessage]);
        }
    }, [agentId]);

    // Initialize client in agent mode
    const client = agentId ? new EmbedAPIClient(agentId, { isAgent: true }) : null;

    const reset = useCallback(() => {
        setMessages(initialMessages);
        setError(null);
        setIsLoading(false);
        if (enableCache && agentId) {
            try {
                sessionStorage.removeItem(`${STORAGE_KEY}${agentId}`);
            } catch (err) {
                console.warn('Failed to clear cached messages:', err);
            }
        }
    }, [initialMessages, agentId, enableCache]);

    // Modify setMessages to respect the limit
    const setMessagesWithLimit = useCallback((newMessages) => {
        if (Array.isArray(newMessages)) {
            setMessages(newMessages.slice(-messageLimit));
        } else {
            setMessages((prev) => {
                const updated = typeof newMessages === 'function' 
                    ? newMessages(prev) 
                    : newMessages;
                return updated.slice(-messageLimit);
            });
        }
    }, [messageLimit]);

    const sendMessage = useCallback(async (content) => {
        if (!agentId) return;

        const userMessage = { role: 'user', content };
        setMessagesWithLimit(prev => [...prev, userMessage]);
        setIsLoading(true);
        setError(null);

        try {
            const response = await client.generate({
                service,
                model,
                messages: [...messages, userMessage],
            });

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response.content
            }]);
        } catch (err) {
            setError(err);
            onError?.(err);
        } finally {
            setIsLoading(false);
        }
    }, [messages, client, service, model, onError, agentId, setMessagesWithLimit]);

    const streamMessage = useCallback(async (content) => {
        if (!agentId) return;

        setIsLoading(true);
        const userMessage = { role: 'user', content };
        setMessagesWithLimit(prev => [...prev, userMessage]);
        setError(null);

        const abortController = new AbortController(); // For stream cancellation
        const signal = abortController.signal;

        try {
            const streamResponse = await client.stream({
                service,
                model,
                messages: [...messages, userMessage],
                signal, // Use the signal for abort handling
            });

            const decoder = new TextDecoder('utf-8');
            currentMessage.current = { role: 'assistant', content: '' };

            for await (const chunk of streamResponse.body) {
                if (signal.aborted) break; // Exit if aborted
                const chunkValue = decoder.decode(chunk);
                const chunks = chunkValue
                    .split(/(\n){2}/)
                    .map((line) => line.replace(/(\n)?^data:\s*/, '').trim())
                    .filter((line) => line !== '' && line !== '[DONE]' && line !== undefined)
                    .map((line) => {
                        return parser.parse(line);
                    });

                for (const chunk of chunks) {
                    // onError?.(chunk);
                    if (chunk?.type === 'chunk') {
                        currentMessage.current.content += chunk.content;
                    } else if (chunk?.type === 'done') {
                        currentMessage.current.cost = chunk.cost;
                        currentMessage.current.tokenUsage = chunk.tokenUsage;
                        setMessages((prev) => [
                            ...prev,
                            { ...currentMessage.current },
                        ]);
                        setIsLoading(false);
                        //reset the currentMessage
                        currentMessage.current = { role: 'assistant', content: '' };
                        break;
                    }
                }
            }

            // Explicitly close the stream if possible
            if (streamResponse.body?.cancel) {
                streamResponse.body.cancel();
            }
        } catch (err) {
            onError?.(err);
            if (signal.aborted) {
                console.log('Stream aborted.');
            } else {
                setError(err);
                onError?.(err);
            }
        } finally {
            setIsLoading(false);
        }

        return () => abortController.abort(); // Cleanup
    }, [messages, client, service, model, onError, agentId, setMessagesWithLimit]);

    return {
        messages,
        isLoading,
        error,
        currentMessage: currentMessage.current,
        sendMessage,
        streamMessage,
        reset,
        clearCache: useCallback(() => {
            if (agentId) {
                try {
                    sessionStorage.removeItem(`${STORAGE_KEY}${agentId}`);
                } catch (err) {
                    console.warn('Failed to clear cached messages:', err);
                }
            }
        }, [agentId]),
        messageCount: messages.length
    };
}